import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { clearStoredToken, getStoredToken, setStoredToken } from "@/lib/authStorage";
import {
  clearApiCache,
  fetchMe,
  loginRequest,
  resetSessionExpiredGuard,
  SESSION_EXPIRED_EVENT,
  type MeUser,
} from "@/lib/api";
import { clearAllSearchHistory } from "@/lib/searchHistory";

export type UserRole = "admin" | "manager" | "marketing" | "user";

export type AuthUser = {
  id: number;
  username: string;
  role: string;
  display_name?: string | null;
  group_id?: number | null;
  group_name?: string | null;
  can_view_all_teams?: boolean;
  visible_group_ids?: number[];
};

type AuthContextValue = {
  user: AuthUser | null;
  role: UserRole;
  isLoading: boolean;
  /** ข้อความอธิบายเมื่อถูกพามาหน้า login — ระบบสะดุดชั่วคราว หรือเซสชันหมดอายุกลางทาง */
  sessionNotice: string | null;
  login: (
    username: string,
    password: string,
    turnstileToken?: string
  ) => Promise<{ ok: boolean; error?: string }>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function mapRole(r: string | undefined): UserRole {
  if (r === "admin" || r === "manager" || r === "marketing") return r;
  return "user";
}

function meToAuthUser(u: MeUser): AuthUser {
  return {
    id: u.id,
    username: u.username,
    role: u.role,
    display_name: u.display_name,
    group_id: u.group_id ?? null,
    group_name: u.group_name ?? null,
    can_view_all_teams: u.can_view_all_teams ?? false,
    visible_group_ids: u.visible_group_ids ?? [],
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionNotice, setSessionNotice] = useState<string | null>(null);

  const refreshUser = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setUser(null);
      return;
    }
    const res = await fetchMe();
    if (res.success && res.user) {
      setSessionNotice(null);
      setUser(meToAuthUser(res.user));
    } else if (res.status === 401 || res.status === 404) {
      // token ผิด/หมดอายุ หรือ user ถูกปิดใช้งาน — ลบ session จริง
      clearStoredToken();
      setSessionNotice(null);
      setUser(null);
    } else {
      // error ชั่วคราว (5xx/network เช่น D1 โหลดเกิน) — เก็บ token ไว้
      // ผู้ใช้จะเห็นหน้า login แต่พอระบบฟื้นแล้วรีเฟรชจะกลับเข้าได้เองไม่ต้อง login ใหม่
      // ไม่เอา error ดิบมาต่อหน้าข้อความ (เคยขึ้นว่า "HTTP 500 — บัญชีของคุณ...") — บอกเป็นภาษาคนล้วน ๆ
      setSessionNotice(
        "ระบบขัดข้องชั่วคราว บัญชีของคุณยังอยู่ รอสักครู่แล้วรีเฟรชหน้า จะกลับเข้าใช้งานได้โดยไม่ต้องเข้าสู่ระบบใหม่"
      );
      setUser(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await refreshUser();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshUser]);

  /**
   * token หมดอายุระหว่างใช้งาน (API ตอบ 401) — ล้าง session ทันทีแล้วให้ RequireAuth
   * พาไปหน้า /login?next=<หน้าเดิม> เอง · เดิมผู้ใช้ต้องเห็นแบนเนอร์ทุก panel แล้วกดออกจากระบบเอง
   * ทำซ้ำได้ปลอดภัย: ถ้า path bootstrap /me ล้าง token ไปแล้ว การล้างซ้ำไม่มีผลข้างเคียง
   */
  useEffect(() => {
    function handleSessionExpired() {
      clearStoredToken();
      clearApiCache();
      setSessionNotice("เซสชันหมดอายุ — กรุณาเข้าสู่ระบบใหม่");
      setUser(null);
    }
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, []);

  const login = useCallback(
    async (username: string, password: string, turnstileToken?: string) => {
      const res = await loginRequest(username, password, turnstileToken);
      if (!res.token || !res.user) {
        return {
          ok: false as const,
          error: res.error || "เข้าสู่ระบบไม่สำเร็จ",
        };
      }
      setStoredToken(res.token);
      // เปิดสวิตช์ให้รอบหน้าที่ token หมดอายุยิง event ได้อีก (guard ตัวเดียวต่อหนึ่งเซสชัน)
      resetSessionExpiredGuard();
      setSessionNotice(null);
      // ตั้ง user ครั้งเดียวหลัง /me ตอบ — ถ้าตั้งจากคำตอบ login ก่อน (ไม่มีทีม / สิทธิ์ดูทุกทีม) หน้า login จะพาเข้าแอปทันที
      // แล้ว guard หน้าข้อมูล (dataAccessOf) ตัดสินจากข้อมูลครึ่งเดียว → หน้า "ไม่มีสิทธิ์" แวบขึ้นมาก่อน /me มาถึง
      // ระหว่างรอ ปุ่มเข้าสู่ระบบยังหมุนอยู่ (submitting) · /me ล้ม = ถือว่าเข้าไม่สำเร็จ (เหมือนตอนเปิดแอปแล้ว /me ล้ม)
      const me = await fetchMe();
      if (!me.success || !me.user) {
        clearStoredToken();
        return {
          ok: false as const,
          error: "ระบบขัดข้องชั่วคราว รอสักครู่แล้วลองเข้าสู่ระบบอีกครั้ง",
        };
      }
      setUser(meToAuthUser(me.user));
      return { ok: true as const };
    },
    []
  );

  const logout = useCallback(() => {
    clearStoredToken();
    clearApiCache();
    // เครื่องที่ใช้ร่วมกัน: คนถัดไปต้องไม่เห็นคำค้นบ่อยของคนก่อน
    clearAllSearchHistory();
    setSessionNotice(null);
    setUser(null);
  }, []);

  const role = mapRole(user?.role);

  const value = useMemo(
    () => ({ user, role, isLoading, sessionNotice, login, logout }),
    [user, role, isLoading, sessionNotice, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
